import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Option,
  SpinButton,
  Spinner,
  Tab,
  TabList,
  Text,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components'
import {
  ArrowLeft16Regular,
  MoreHorizontal20Regular,
  People16Regular,
} from '@fluentui/react-icons'
import {
  useAddTeamMember,
  useConfirmWithdrawal,
  useDeclineWithdrawal,
  useFreehandSuggestions,
  useDeleteDemand,
  useDemand,
  useRestoreDemand,
  useStaffDemand,
  useTransitionDemand,
  useUpdateDemand,
} from '../hooks/useDemands'
import { useApplications, useCreateApplication, useMyApplications } from '../hooks/useApplications'
import { useMyInvitations, useRespondToInvitation } from '../hooks/useInvitations'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useDemandReadiness } from '../hooks/useDemandReadiness'
import { useMentionableUsers } from '../hooks/useMentionableUsers'
import { useTalentDirectory } from '../hooks/useTalentDirectory'
import type { MentionableUser } from '../hooks/useMentionableUsers'
import { useCreateComment } from '../hooks/useComments'
import { EditableField } from '../components/EditableField'
import { PersonPicker } from '../components/PersonPicker'
import { FreehandLinkControl } from '../components/FreehandLinkControl'
import { useToast } from '../components/ToastProvider'
import { RouteDemandDialog } from '../components/RouteDemandDialog'
import type {
  Demand,
  DemandStage,
  DemandTransitionInput,
  DemandUpdateInput,
} from '../types/demand'
import { OverviewTab } from './demand-detail/OverviewTab'
import { focusAreaWithName } from '../types/focus_area'
import { DsaiAssignment } from '../components/DsaiAssignment'
import { BusinessCaseTab } from './demand-detail/BusinessCaseTab'
import { TalentTab } from './demand-detail/TalentTab'
import { TimelineTab } from './demand-detail/TimelineTab'
import { DemandDiscussion } from '../components/DemandDiscussion'
import {
  EMPTY_PRIORITY_DRAFT,
  PriorityTab,
  isPriorityDraftDirty,
  type PriorityDraft,
} from './demand-detail/PriorityTab'

import { ResolveDialog } from '../components/ResolveDialog'
import { ReopenDialog } from '../components/ReopenDialog'
import { WithdrawDialog } from '../components/WithdrawDialog'
import { ChangeStageDialog } from './demand-detail/ChangeStageDialog'
import { DeleteDemandDialog } from './demand-detail/DeleteDemandDialog'
import { TransitionDialog, type BroadcastAudience } from '../components/TransitionDialog'
import { ApplyDialog } from '../components/ApplyDialog'
import { apiErrorMessage, blobErrorMessage } from '../lib/apiError'
import { demandListReturnFrom, rememberDemandList } from '../lib/demandListReturn'
import { ErrorCard } from '../components/ErrorCard'
import { parseStageWarnings, type StageWarnings } from '../lib/stageWarnings'
import { StageWarningDialog } from '../components/StageWarningDialog'
import api from '../lib/api'
import { useEmailArrival } from '../hooks/useEmailArrival'
import { downloadBlob } from '../lib/download'
import {
  STAGE_LABELS,
  closureTypeFor,
  resolutionTypeLabel,
  priorityBucketColor,
  priorityTierLabel,
  DEMAND_TYPE_LABELS,
  SUPPORT_MODE_LABELS,
} from '../lib/labels'
import { slaTone, slaTargetLabel, slaDaysLeft } from '../lib/demandSla'
import { deriveStaffing, STAFFING_STATUS_LABELS } from '../lib/staffing'
import { applicationsOpen as acceptsApplications, applyBy } from '../lib/applicationsOpen'
import { InfoHint } from '../components/planning/PlanParts'
import { bayerColors, bucketColors } from '../theme'

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '1280px',
  },
  back: {
    alignSelf: 'flex-start',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  breadcrumb: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  divider: {
    color: tokens.colorNeutralForeground4,
  },
  titleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  title: {
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: 700,
    color: bayerColors.navy,
    margin: 0,
    flex: 1,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  // Window-closes banner (mockup §7004-7006). Inline so it sits inside the
  // header metadata row but pops on amber/red as the deadline approaches.
  windowMsg: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  completenessRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  completenessMeter: {
    width: '48px',
    height: '5px',
    background: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
  },
  completenessFill: {
    height: '100%',
    transitionProperty: 'width',
    transitionDuration: '200ms',
  },
  completenessGood: {
    background: tokens.colorPaletteGreenForeground2,
  },
  completenessMedium: {
    background: tokens.colorPaletteDarkOrangeForeground1,
  },
  completenessLow: {
    background: tokens.colorPaletteRedForeground1,
  },
  tabList: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  body: {
    paddingTop: tokens.spacingVerticalM,
  },
  // ── Detail grid: main content + sidebar ──
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 280px',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
  },
  sideSection: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalM,
  },
  sideSectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    margin: 0,
    marginBottom: tokens.spacingVerticalS,
  },
  sideKvRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalXS} 0`,
    fontSize: '13px',
  },
  sideKvKey: {
    color: tokens.colorNeutralForeground3,
  },
  sideKvValue: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
})

type TabKey = 'overview' | 'business' | 'talent' | 'timeline' | 'priority'

interface DemandDetailProps {
  /** Optional override for tests / storybook. Otherwise pulled from the URL. */
  demandId?: number | string
}

// Source of truth: lib/labels.ts. Local alias keeps existing call sites.
const STAGE_LABEL: Record<DemandStage, string> = STAGE_LABELS

const STAGE_COLOR: Record<DemandStage, string> = {
  new: '#64748b',
  under_review: '#f59e0b',
  open_to_application: '#0891b2',
  staffed: '#8b5cf6',
  resolved: bucketColors.quickWins,
  not_prioritized: bucketColors.declined,
  // New-model stages (FE expand, Inc-3). Renames reuse their old colour;
  // scoping/triaging/resourcing get their own.
  scoping: '#4f7cac',
  triaging: '#f59e0b',
  resourcing: '#0891b2',
  parking: bucketColors.declined,
  closed: bucketColors.quickWins,
}

// The private PriorityBucket map + canonicalizeBucket that lived here were
// the last surviving copy of the derivation labels.ts:229 documents removing
// from DemandList and DemandKanban (#1156). The server sends the derived
// bucket on `demand.priority_bucket`; this page reads that, like every other
// surface — so a parked demand no longer shows its stale stored override in
// the header while the Kanban shows it in Parking Lot.

// Closure chip color from the derived top-level closure_type.
function closureColor(resolution: string | null | undefined): 'success' | 'danger' | 'informative' {
  const t = closureTypeFor(resolution)
  if (t === 'promoted') return 'success'
  if (t === 'rejected') return 'danger'
  return 'informative'
}

// Stage urgency comes from lib/demandSla (#1156): this page's private
// STAGE_THRESHOLDS listed only the LEGACY stages, so every current-model
// demand (scoping/resourcing/...) rendered with no urgency at all while the
// sidebar promised a target.

function daysBetween(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.floor((now.getTime() - t) / 86_400_000)
}

function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.ceil((t - now.getTime()) / 86_400_000)
}

type Urgency = 'amber' | 'red' | null

function stageUrgency(stage: DemandStage | string, days: number | null): Urgency {
  if (days === null) return null
  const tone = slaTone(stage, days)
  return tone === 'green' ? null : tone
}


// The intake-completeness heuristic that lived here (one of #998's three
// competing definitions, and a byte-copy of CouncilTriage's) is replaced by
// the readiness report — the expectations engine read sideways, so the
// number shown IS the 409 the next move would raise (#1207).

// The step `?do=advance` takes from each stage (STEP_ADVANCEMENTS). A legacy
// `under_review` row reads as Scoping (normalize_stage), so it steps the same.
const ROUNDUP_NEXT_STAGE: Partial<Record<string, DemandStage>> = {
  new: 'scoping',
  scoping: 'triaging',
  under_review: 'triaging',
  triaging: 'resourcing',
}

export function DemandDetail({ demandId: propId }: DemandDetailProps = {}) {
  const params = useParams<{ id?: string }>()
  const demandId = propId ?? params.id
  const [searchParams, setSearchParams] = useSearchParams()

  // Unsaved priority edits live here, not inside PriorityTab: the tab body is
  // conditionally rendered, so a switch to Timeline and back used to unmount
  // the buffer and silently empty it (#1247). Reset per demand — a client-side
  // navigation to another demand must not carry someone else's draft over.
  const [priorityDraft, setPriorityDraft] = useState<PriorityDraft>(EMPTY_PRIORITY_DRAFT)
  useEffect(() => {
    setPriorityDraft(EMPTY_PRIORITY_DRAFT)
  }, [demandId])
  const styles = useStyles()
  const effectiveUser = useEffectiveUser()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const listReturn = useMemo(() => demandListReturnFrom(location.state), [location.state])

  useEffect(() => {
    if (listReturn) rememberDemandList(listReturn)
  }, [listReturn])

  function backToList() {
    if (listReturn || window.history.length > 1) navigate(-1)
    else navigate('/demands')
  }

  useEmailArrival(demandId, searchParams.get('src'))

  // Role gating uses the *effective* user so admins exercising
  // impersonation see the surface the impersonated role would see. The
  // real-admin identity stays on useAuth() for telemetry purposes.
  const isCouncil =
    effectiveUser?.role === 'council' ||
    effectiveUser?.role === 'admin' ||
    effectiveUser?.role === 'super_admin'

  const { data: demand, isLoading, isError, error, refetch } = useDemand(demandId)
  // 404 is an ANSWER here, not a failure — see the early return below.
  const isNotFound =
    (error as { response?: { status?: number } } | null)?.response?.status === 404
  const updateDemand = useUpdateDemand()
  const transitionDemand = useTransitionDemand()
  const staffDemand = useStaffDemand()
  const deleteDemand = useDeleteDemand()

  // Council only (#1044). This response carries applicant emails, cover letters
  // and council feedback; it used to be fetched for every visitor, so a talent
  // opening any demand received all of it whether or not the UI drew it. The
  // backend now refuses, but not asking is the half that keeps it honest —
  // a 403 in the console on every page load would be its own kind of noise.
  const { data: applicationsResponse } = useApplications(
    demand && isCouncil ? { demand_id: demand.id } : undefined,
  )
  const applications = applicationsResponse?.items ?? []

  // Talent: fetch own applications to show decision status on this demand
  const { data: myAppsResponse } = useMyApplications()
  const myApplication = demand
    ? (myAppsResponse?.items ?? []).find((a) => a.demand_id === demand.id)
    : undefined

  // Talent: an outstanding invitation to THIS demand. This page used to know
  // nothing about invitations and offered "Apply to this demand" regardless —
  // which is how an invited talent ended up in a review queue instead of on the
  // team, having spent the invitation to get there. The backend no longer
  // punishes that, but the CTA still has to say which thing is on offer: being
  // asked for is not the same as putting your hand up, and only one of them
  // works when the application window has closed.
  const { data: myInvitations } = useMyInvitations()
  const myPendingInvitation = demand
    ? (myInvitations ?? []).find((i) => i.demand_id === demand.id && i.status === 'pending')
    : undefined
  const respondInvitation = useRespondToInvitation()

  // `?tab=` makes a tab shareable (a timeline permalink needs the tab to open),
  // and it is read every render rather than copied into state: a `useState`
  // initialiser runs once, so browser Back changed the address bar and left the
  // page on the tab it was already showing. An invalid or role-hidden value
  // degrades via `effectiveTab` below.
  const tabParam = searchParams.get('tab')
  const tab: TabKey =
    tabParam === 'overview' ||
    tabParam === 'business' ||
    tabParam === 'talent' ||
    tabParam === 'timeline' ||
    tabParam === 'priority'
      ? tabParam
      : 'overview'

  function selectTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if (next === 'overview') p.delete('tab')
        else p.set('tab', next)
        return p
      },
      { replace: true, state: location.state },
    )
  }
  const [resolveOpen, setResolveOpen] = useState(false)
  const [reopenOpen, setReopenOpen] = useState(false)
  const [withdrawOpen, setWithdrawOpen] = useState(false)
  // Council-level Route action, same dialog as the audit queue (#1319).
  const [routeDialogOpen, setRouteDialogOpen] = useState(false)
  const [pendingTransition, setPendingTransition] = useState<DemandStage | null>(null)
  const [changeStageOpen, setChangeStageOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [applyOpen, setApplyOpen] = useState(false)
  // Unmet stage expectations (#988). A 409, not a refusal: the dialog offers
  // "Continue anyway", which re-sends the transition with the acknowledgement.
  // `stageWarnings` holds what to show; `warnedTransition` holds the move to
  // retry once the user confirms.
  const [stageWarnings, setStageWarnings] = useState<StageWarnings | null>(null)
  const [warnedTransition, setWarnedTransition] = useState<{
    body: DemandTransitionInput
    onDone?: () => void
  } | null>(null)
  // Caretaker-required prompt (UR → OTA gate). When the user clicks
  // "Post for Applications" without a caretaker assigned, this dialog
  // surfaces a picker; after assignment, the pending transition fires.
  const [caretakerPromptOpen, setCaretakerPromptOpen] = useState(false)
  const [pickedCaretakerId, setPickedCaretakerId] = useState<number | null>(null)
  const createApplication = useCreateApplication()

  // Delete is FA-scoped on the backend (council = own FA only;
  // admin/super_admin = any). Gate the menu item by the same rule so
  // we don't surface a button that's going to 403.
  const canDelete = !!demand && !!effectiveUser && (
    effectiveUser.role === 'admin' ||
    effectiveUser.role === 'super_admin' ||
    (effectiveUser.role === 'council' &&
      !!effectiveUser.focus_area_code &&
      effectiveUser.focus_area_code === demand.focus_area_code)
  )

  function handleDelete(reason: string) {
    if (!demand) return
    deleteDemand.mutate(
      { id: demand.id, reason },
      {
        onSuccess: () => {
          toast.showToast({ title: 'Demand deleted', intent: 'success' })
          setDeleteOpen(false)
          navigate('/demands')
        },
        onError: (err: unknown) => {
          toast.showToast({
            title: 'Delete failed',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  // Admin-only: restoring a soft-deleted demand clears deleted_at and
  // appends an audit row (Timeline shows the restore). The button is
  // surfaced via the deleted-banner; non-admins never see a deleted
  // demand in the first place (the GET 404s for them).
  const restoreDemand = useRestoreDemand()
  const isAdminLike =
    effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'

  // What this viewer may do is the SERVER's answer (`demand.viewer`), computed
  // from the rules its endpoints enforce. This page used to work it out from
  // role, focus area and caretaker id, under comments saying "mirror the
  // backend" — and when #995 stopped scoping council edits to a focus area, it
  // went on hiding controls the API accepted. Nothing here may re-derive a
  // permission (#1268); a missing answer means no.
  //
  // `canEditFields` gates the PATCH-backed surface (inline fields, people
  // pickers, step buttons) — council, admin, and this demand's caretaker.
  // `canEditDemand` gates the council surface (change stage, reopen, team
  // management, tags). `canClose` is closing on its own, which a caretaker
  // holds without the rest of that surface (#1268).
  const canEditDemand = demand?.viewer?.can_manage ?? false
  const canEditFields = demand?.viewer?.can_edit ?? false
  // The request's own words: council and the caretaker as above, and the
  // requestor for their own open demand. Reaches title, description and the
  // form's answers only — the server refuses anything else from them.
  const canEditRequest = demand?.viewer?.can_edit_request ?? canEditFields
  const canClose = demand?.viewer?.can_close ?? false

  // The checklist between moves (#1207). Edit-trio only; the endpoint 403s
  // everyone else, so the fetch is gated the same way.
  const { data: readiness, isPending: readinessPending } = useDemandReadiness(
    demand?.id,
    canEditFields,
  )

  // Mentionable list for the caretaker-required prompt. Same hook the
  // sidebar uses (React Query dedupes); fetched only for viewers who can
  // edit — everyone else renders names from the demand itself (#1153).
  const { data: caretakerCandidates = [] } = useMentionableUsers(
    demand?.focus_area_code,
    canEditFields,
  )

  const confirmWithdrawal = useConfirmWithdrawal()
  const declineWithdrawal = useDeclineWithdrawal()

  // Withdraw: the requestor pulls their own live request, or council/admin
  // does it on their behalf; never on a closed demand. The server's answer.
  const isRequestor =
    !!effectiveUser?.id && demand?.submitter_user_id === effectiveUser.id
  const canWithdraw = demand?.viewer?.can_withdraw ?? false
  const pendingWithdrawal = !!demand?.withdrawal_requested_at

  function handleConfirmWithdrawal() {
    if (!demand) return
    confirmWithdrawal.mutate(
      { id: demand.id },
      {
        onSuccess: () => toast.showToast({ title: 'Withdrawal confirmed — demand closed' }),
        onError: (err) =>
          toast.showToast({
            title: 'Failed to confirm withdrawal',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  function handleDeclineWithdrawal() {
    if (!demand) return
    declineWithdrawal.mutate(
      { id: demand.id },
      {
        onSuccess: () => toast.showToast({ title: 'Withdrawal declined' }),
        onError: (err) =>
          toast.showToast({
            title: 'Failed to decline withdrawal',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  function handleRestore() {
    if (!demand) return
    restoreDemand.mutate(demand.id, {
      onSuccess: () => {
        toast.showToast({ title: 'Demand restored', intent: 'success' })
      },
      onError: (err: unknown) => {
        toast.showToast({
          title: 'Restore failed',
          body: apiErrorMessage(err),
          intent: 'error',
        })
      },
    })
  }

  const visibleTabs = useMemo<Array<{ key: TabKey; label: string; count?: number }>>(() => {
    const tabs: Array<{ key: TabKey; label: string; count?: number }> = [
      { key: 'overview', label: 'Overview' },
    ]
    if (isCouncil) tabs.push({ key: 'business', label: 'Business case' })
    tabs.push({ key: 'talent', label: 'Talent', count: isCouncil ? (applications.length || undefined) : undefined })
    if (isCouncil) tabs.push({ key: 'timeline', label: 'Timeline' })
    if (isCouncil) {
      tabs.push({ key: 'priority', label: 'Priority' })
    }
    return tabs
  }, [isCouncil, applications.length])

  // If the active tab disappeared (e.g. stage transition), fall back to overview.
  const effectiveTab = visibleTabs.some((t) => t.key === tab) ? tab : 'overview'

  function handleSave(path: string, value: string | string[] | number) {
    if (!demand) return
    const patch: DemandUpdateInput = {}
    // Allow dotted paths (e.g. "business.issue_problem") to map to top-level
    // backend fields. The substrate keeps business-case fields flat on Demand,
    // so we strip the prefix here.
    const flatKey = path.replace(/^business\./, '') as keyof DemandUpdateInput
    ;(patch as Record<string, unknown>)[flatKey] = value
    updateDemand.mutate(
      { id: demand.id, data: patch },
      {
        onError: (err) =>
          toast.showToast({
            title: 'Failed to save change',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )

    // The parking-lot bucket <-> parking stage coupling is GONE (#988 §7).
    // It fired a second, untransacted request after this PATCH had already
    // committed, so a failure left `bucket = High Priority` with
    // `stage = Parking Lot` — the exact state the shared name makes
    // unexplainable. Parking Lot is now DERIVED from the stage, so there is no
    // second field to keep in step and no second request to fail. To park a
    // demand, move its stage.
  }

  // The Priority tab's whole edit buffer in ONE request (#1155) — rejected
  // saves rethrow so the tab keeps the buffer for a retry.
  function handleSaveMany(patch: Record<string, string | number>): Promise<unknown> {
    if (!demand) return Promise.resolve()
    return updateDemand
      .mutateAsync({ id: demand.id, data: patch as DemandUpdateInput })
      .catch((err) => {
        toast.showToast({
          title: 'Failed to save changes',
          body: apiErrorMessage(err),
          intent: 'error',
        })
        throw err
      })
  }

  function handleTransition(toStage: DemandStage, _message: string) {
    if (!demand) return
    // Prompt for a caretaker only where the backend actually requires one:
    // LEAVING New, and satisfied by a freehand name as well as a directory id
    // (`_has_caretaker`, routers/demands.py). This used to test
    // `toStage === 'resourcing' && !demand.caretaker_id`, which was wrong twice
    // over — it refused a Triaging → Resourcing move the server performs
    // happily, and it refused it for the ~26 demands whose caretaker is a
    // freehand name, while "Change stage… → Resourcing" right beside it did the
    // same transition with no check at all.
    const hasCaretaker = !!(demand.caretaker_id || demand.caretaker_name?.trim())
    if (demand.stage === 'new' && !hasCaretaker) {
      setPickedCaretakerId(null)
      setCaretakerPromptOpen(true)
      return
    }
    setPendingTransition(toStage)
  }

  // One click from the Monday stalled-demand mail: `?do=` names the next step
  // the mail suggested (services/stale_demand_checker.py, suggest()). It opens
  // the same dialog the button on this page opens — nothing happens without
  // that confirmation — and only when this viewer may take the step;
  // otherwise the page simply opens. Handled once per link, then dropped from
  // the address so a refresh or Back does not reopen the dialog.
  const doParam = searchParams.get('do')
  const handledDo = useRef<string | null>(null)
  useEffect(() => {
    if (!doParam || !demand) return
    // The checklist is a second query that starts once the demand is here;
    // wait for it, or the scroll target does not exist yet (Copilot on #1503).
    // Wait only until it settles: if it fails, the link is used up as it is
    // and the page stays open (Copilot on #1531).
    if (doParam === 'checklist' && canEditFields && readinessPending) return
    const key = `${demand.id}:${doParam}`
    if (handledDo.current === key) return
    handledDo.current = key
    const next = ROUNDUP_NEXT_STAGE[demand.stage]
    if (doParam === 'close' && canClose) setResolveOpen(true)
    else if (doParam === 'route' && canEditDemand) setRouteDialogOpen(true)
    else if (doParam === 'advance' && canEditFields && next) handleTransition(next, '')
    else if (doParam === 'caretaker') {
      document.getElementById('demand-people')?.scrollIntoView?.({ block: 'center' })
    } else if (doParam === 'checklist') {
      document.getElementById('demand-readiness')?.scrollIntoView?.({ block: 'center' })
    }
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.delete('do')
        return p
      },
      { replace: true, state: location.state },
    )
    // Keyed on the link and the loaded demand; the ref makes it once per link.
    // handleTransition is rebuilt every render, so listing it would re-run
    // this on every render for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doParam, demand, canClose, canEditDemand, canEditFields, readinessPending])

  function confirmCaretakerAssignment() {
    if (!demand || pickedCaretakerId == null) return
    const user = caretakerCandidates.find((u) => u.id === pickedCaretakerId)
    if (!user) return
    updateDemand.mutate(
      {
        id: demand.id,
        data: { caretaker_id: user.id, caretaker_name: user.name },
      },
      {
        onSuccess: () => {
          setCaretakerPromptOpen(false)
          setPendingTransition('resourcing')
        },
        onError: (err) => {
          toast.showToast({
            title: 'Failed to assign caretaker',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  /** "Continue anyway" — re-send the move with the acknowledgement attached.
   *  The backend refuses a partial list, so it goes back verbatim. */
  function continuePastWarnings(acknowledgeWith: string[]) {
    if (!demand || !warnedTransition) return
    const { body, onDone } = warnedTransition
    transitionDemand.mutate(
      { id: demand.id, body: { ...body, acknowledged_warnings: acknowledgeWith } },
      {
        onSuccess: () => {
          toast.showToast({
            title: `Stage changed to ${STAGE_LABELS[body.to_stage as DemandStage]}`,
          })
          setStageWarnings(null)
          setWarnedTransition(null)
          onDone?.()
        },
        onError: (err: unknown) => {
          toast.showToast({ title: apiErrorMessage(err, 'Stage change failed'), intent: 'error' })
          setStageWarnings(null)
          setWarnedTransition(null)
        },
      },
    )
  }

  function dismissWarnings() {
    setStageWarnings(null)
    setWarnedTransition(null)
  }

  function confirmTransition(notes: string, broadcastAudience?: BroadcastAudience) {
    if (!demand || !pendingTransition) return
    const hadDemandId = !!demand.demand_id
    transitionDemand.mutate(
      { id: demand.id, body: { to_stage: pendingTransition, notes, broadcast_audience: broadcastAudience } },
      {
        onSuccess: (updated) => {
          toast.showToast({ title: `Stage changed to ${STAGE_LABELS[pendingTransition]}` })

          // Surface the auto-minted demand_id when it gets assigned for
          // the first time (UR → OTA, or the rare UR → staffed admin path).
          if (!hadDemandId && updated?.demand_id) {
            toast.showToast({
              title: `Demand ID assigned: ${updated.demand_id}`,
              intent: 'success',
            })
          }

          setPendingTransition(null)
        },
        onError: (err: unknown) => {
          // A 409 means the move is still available — the demand is just
          // missing things it was expected to have by now. Offer to continue.
          const warnings = parseStageWarnings(err)
          if (warnings) {
            setStageWarnings(warnings)
            setWarnedTransition({
              body: {
                to_stage: pendingTransition,
                notes,
                broadcast_audience: broadcastAudience,
              },
            })
            setPendingTransition(null)
            return
          }
          // Anything else is a Blocked rule. apiErrorMessage now decodes the
          // structured `{code, message}` shape, so B4's caretaker block
          // explains itself instead of showing "status code 400" (#977).
          toast.showToast({ title: apiErrorMessage(err, 'Stage change failed'), intent: 'error' })
          setPendingTransition(null)
        },
      },
    )
  }

  function handleStageOverride(
    toStage: DemandStage,
    reason: string,
    broadcastAudience?: BroadcastAudience,
  ) {
    if (!demand) return
    const hadDemandId = !!demand.demand_id
    transitionDemand.mutate(
      {
        id: demand.id,
        body: {
          to_stage: toStage,
          notes: reason || undefined,
          broadcast_audience: broadcastAudience,
        },
      },
      {
        onSuccess: (updated) => {
          toast.showToast({
            title: `Stage changed to ${STAGE_LABELS[toStage]}`,
            intent: 'success',
          })
          if (!hadDemandId && updated?.demand_id) {
            toast.showToast({
              title: `Demand ID assigned: ${updated.demand_id}`,
              intent: 'success',
            })
          }
          setChangeStageOpen(false)
        },
        onError: (err: unknown) => {
          // A 409 is not a refusal — the move is still available, the demand is
          // just missing things it was expected to have by now. This handler
          // used to go straight to apiErrorMessage, which returns only
          // `detail.message`: so the backend's own "You can continue anyway"
          // was recited as a red toast, `detail.violations` was thrown away,
          // and nothing offered the continue it had just promised. That is
          // ticket #19, reported word for word — "it says I can skip the
          // missing information, but still does not move the demand", and "I
          // have no clue what is missing".
          //
          // `confirmTransition` in this same file already did this correctly.
          // Two handlers, one wired and one not; this reuses that machinery
          // rather than growing a third copy of it.
          const warnings = parseStageWarnings(err)
          if (warnings) {
            setStageWarnings(warnings)
            setWarnedTransition({
              body: {
                to_stage: toStage,
                notes: reason || undefined,
                broadcast_audience: broadcastAudience,
              },
              onDone: () => setChangeStageOpen(false),
            })
            return
          }
          // Anything else is a Blocked rule and stays a plain error — only the
          // 409 is dismissible.
          toast.showToast({
            title: 'Stage change failed',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div className={styles.loading} data-testid="demand-loading">
        <Spinner label="Loading demand…" />
      </div>
    )
  }

  // A 404 is the rule being applied, not the app failing.
  //
  // GET /demands/{id} answers 404 both when the demand does not exist and when
  // the caller may not see it — `_talent_visibility_filter` cloaks the second
  // as the first so a talent cannot enumerate cross-FA demands by ID. That
  // cloak is deliberate and this does not lift it: the copy below is the same
  // whichever case the reader is in, and names no property of the demand.
  //
  // What it does fix is the reader's conclusion. "Could not load demand" —
  // identical to what a 500, a dropped connection and an expired session
  // produced — reads as a broken page, so someone who opened a link to a demand
  // still in Triaging reported the app was down. It was working exactly as
  // designed, and said nothing that would let them know that.
  if (isNotFound) {
    return (
      <div data-testid="demand-unavailable">
        <ErrorCard
          title="Can't open this demand"
          message={
            isCouncil
              ? // Council read access is unrestricted, so for them a 404 really
                // does mean gone. Offering the visibility explanation would send
                // them hunting for a permission they already hold.
                "This demand doesn't exist. It may have been deleted, or the link may be wrong."
              : "It either doesn't exist, or it hasn't been opened to you yet. A demand becomes visible once it's posted for applications, or if a focus area council invites you to it."
          }
        >
          {listReturn ? (
            <Button onClick={backToList}>Back to demands</Button>
          ) : (
            <Link to={isCouncil ? '/demands' : '/opportunities'}>
              {isCouncil ? 'Back to demands' : 'Browse open opportunities'}
            </Link>
          )}
        </ErrorCard>
      </div>
    )
  }

  if (isError || !demand) {
    return (
      <ErrorCard
        title="Could not load demand"
        message={apiErrorMessage(error, 'Something went wrong loading this demand.')}
        retry={() => void refetch()}
      >
        {listReturn && <Button onClick={backToList}>Back to demands</Button>}
      </ErrorCard>
    )
  }

  const stageLabel = STAGE_LABEL[demand.stage] ?? demand.stage
  const resolutionLabel = demand.resolution_type
    ? resolutionTypeLabel(demand.resolution_type)
    : null
  const daysInStage = daysBetween(demand.stage_entered_at ?? demand.submission_date)
  const urgency = stageUrgency(demand.stage, daysInStage)
  const windowDays = daysUntil(demand.application_closes_at)
  // One handler behind both places Mark Staffed can be offered (toolbar, card).
  const markStaffed = () =>
    staffDemand.mutate(
      { id: demand.id, body: {} },
      {
        onSuccess: () => toast.showToast({ title: 'Marked staffed' }),
        // The backend's 400 names the fix ("set a caretaker first") — #977.
        onError: (err) =>
          toast.showToast({
            title: 'Failed to mark staffed',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  return (
    <div className={styles.page} data-testid="demand-detail">
      {/* The handler belongs on the Button, not the wrapper: a role="link" span
          with no tabIndex announces a control the keyboard cannot reach. */}
      <span className={styles.back}>
        <Button
          appearance="transparent"
          size="small"
          icon={<ArrowLeft16Regular />}
          onClick={backToList}
        >
          {listReturn ? 'Back to demands' : 'Back'}
        </Button>
      </span>

      {demand.deleted_at && isAdminLike && (
        <MessageBar intent="warning" data-testid="demand-deleted-banner">
          <MessageBarBody>
            <strong>This demand was deleted</strong>
            {demand.deletion_reason ? ` — “${demand.deletion_reason}”` : ''}
            {demand.deleted_at
              ? ` on ${new Date(demand.deleted_at).toLocaleString()}`
              : ''}
            . Council members can&rsquo;t see it. Restore to bring it back.
          </MessageBarBody>
          <Button
            size="small"
            appearance="primary"
            onClick={handleRestore}
            disabled={restoreDemand.isPending}
            data-testid="demand-restore"
          >
            {restoreDemand.isPending ? 'Restoring…' : 'Restore'}
          </Button>
        </MessageBar>
      )}

      {demand.disposition === 'digital_council' && (
        <MessageBar intent="info" data-testid="routed-onward-banner">
          {/* Cluster leads may assign without holding the council edit surface. */}
          {isCouncil || demand.viewer?.can_assign_dsai ? (
            <MessageBarBody>
              <strong>Routed to DSAI.</strong>{isCouncil && ' Reason in Timeline.'}{' '}
              <Link to={`/dsai?request=${demand.id}`} data-testid="routed-see-dc-view">
                Open DSAI status and comments →
              </Link>
              {/* General editors use the People card; leads need their narrow picker here. */}
              <div style={{ marginTop: 8 }}>
                <DsaiAssignment
                  demandId={demand.id}
                  cluster={demand.dsai_cluster}
                  caretakerId={demand.caretaker_id}
                  caretakerName={demand.caretaker_name}
                  canAssign={demand.viewer?.can_assign_dsai ?? false}
                  showCaretaker={!canEditFields}
                />
              </div>
            </MessageBarBody>
          ) : (
            <MessageBarBody>
              <strong>Passed to DSAI.</strong> This request is not a Data &amp; Analytics Council
              matter, so the right DSAI team will take it from here. There is nothing you need
              to do.
            </MessageBarBody>
          )}
        </MessageBar>
      )}

      {pendingWithdrawal && (
        <MessageBar intent="warning" data-testid="pending-withdrawal">
          <MessageBarBody>
            <strong>Withdrawal requested</strong>
            {/* The free-text reason can carry internal rationale — show it only
                to council-who-can-act and the requestor, not every viewer. */}
            {(canEditDemand || isRequestor) && demand.withdrawal_reason
              ? ` — “${demand.withdrawal_reason}”`
              : ''}
            {!canEditDemand && ' — awaiting council review.'}
          </MessageBarBody>
          {canEditDemand && (
            <>
              <Button
                size="small"
                appearance="primary"
                onClick={handleConfirmWithdrawal}
                disabled={confirmWithdrawal.isPending || declineWithdrawal.isPending}
                data-testid="withdrawal-confirm"
              >
                Confirm — close
              </Button>
              <Button
                size="small"
                onClick={handleDeclineWithdrawal}
                disabled={confirmWithdrawal.isPending || declineWithdrawal.isPending}
                data-testid="withdrawal-decline"
              >
                Decline
              </Button>
            </>
          )}
        </MessageBar>
      )}

      {urgency === 'red' &&
        (demand.stage === 'scoping' ||
          demand.stage === 'triaging' ||
          demand.stage === 'resourcing') && (
        <MessageBar intent="warning" data-testid="sla-breach-banner">
          <MessageBarBody>
            <strong>Overdue</strong> — this demand has been in &ldquo;{stageLabel}&rdquo; for {daysInStage} days (target: {slaTargetLabel(demand.stage) ?? "14 days"}).
            Consider escalating or taking action.
          </MessageBarBody>
        </MessageBar>
      )}

      <header className={styles.header}>
        <div className={styles.breadcrumb}>
          <span data-testid="demand-id-pill">{demand.demand_id || `DEM-${demand.id}`}</span>
          <span className={styles.divider}>·</span>
          <span data-testid="demand-focus-area">{focusAreaWithName(demand.focus_area_code)}</span>
          {demand.department && (
            <>
              <span className={styles.divider}>·</span>
              <span>{demand.department}</span>
            </>
          )}
          {demand.source_system === 'sharepoint' && demand.source_reference_id && (
            <>
              <span className={styles.divider}>·</span>
              <span title={`Imported from SharePoint item #${demand.source_reference_id}`}>
                Source: SharePoint #{demand.source_reference_id}
              </span>
            </>
          )}
        </div>

        {demand.possible_duplicate_of != null && (
          <div
            data-testid="demand-dup-flag"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 6,
              background: '#fff3e0',
              color: '#a25e00',
              fontSize: 13,
              fontWeight: 600,
              alignSelf: 'flex-start',
            }}
            title={
              demand.dup_match_score
                ? `${Math.round(demand.dup_match_score)}% title match`
                : undefined
            }
          >
            ⚠ Possible duplicate of{' '}
            <Link
              to={`/demand/${demand.possible_duplicate_of}`}
              style={{ color: '#a25e00', textDecoration: 'underline' }}
            >
              #{demand.possible_duplicate_of}
            </Link>
            {demand.dup_match_score
              ? ` · ${Math.round(demand.dup_match_score)}% match`
              : ''}
          </div>
        )}

        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            <EditableField
              value={demand.title}
              path="title"
              onSave={handleSave}
              disabled={!canEditRequest}
              placeholder="Demand title…"
              ariaLabel="Demand title"
            />
          </h1>
        </div>

        <div className={styles.metaRow}>
          <span
            data-testid="stage-badge"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 10px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              background: `${STAGE_COLOR[demand.stage]}18`,
              color: STAGE_COLOR[demand.stage],
              border: `1px solid ${STAGE_COLOR[demand.stage]}40`,
            }}
          >
            {stageLabel}
          </span>
          {(() => {
            const bucket = demand.priority_bucket ?? null
            const color = priorityBucketColor(bucket)
            if (!bucket || !color) return null
            return (
              <span
                data-testid="priority-bucket-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 10px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  background: `${color}18`,
                  color,
                  border: `1px solid ${color}40`,
                }}
              >
                {priorityTierLabel(bucket)}
              </span>
            )
          })()}
          {demand.stage === 'closed' && resolutionLabel && (
            <Badge
              appearance="tint"
              color={closureColor(demand.resolution_type)}
              data-testid="resolution-badge"
            >
              {resolutionLabel}
            </Badge>
          )}
          {urgency && daysInStage !== null && (
            <Badge
              appearance="filled"
              color={urgency === 'red' ? 'danger' : 'warning'}
              data-testid="urgency-badge"
              title={`In ${stageLabel} for ${daysInStage} days`}
            >
              {daysInStage}d in stage
            </Badge>
          )}
          {readiness && readiness.next_stage && canEditFields && (() => {
            const met = readiness.items.filter((i) => i.met).length
            const total = readiness.items.length
            const done = met === total
            return (
              <span
                className={styles.completenessRow}
                data-testid="readiness-pill"
                title={
                  done
                    ? `Everything expected by ${readiness.next_stage} is filled in`
                    : `${total - met} item(s) the move to ${readiness.next_stage} will flag — see the Readiness card`
                }
                style={{ color: done ? 'var(--green-fg1, #107c10)' : undefined }}
              >
                {met}/{total} ready
              </span>
            )
          })()}
          {demand.stage === 'resourcing' &&
            (demand.application_closes_at || demand.applications_closed_at) &&
            (() => {
              // The deadline is a target; only council or the caretaker close
              // applications (2026-10-16). So past the deadline it still says open.
              const closed = !!demand.applications_closed_at
              const ahead = applyBy(demand.application_closes_at)
              const tone = closed
                ? (['var(--colorNeutralBackground3)', 'var(--colorNeutralForeground2)'] as const)
                : !ahead
                  ? (['var(--yellow-bg1)', 'var(--yellow-fg1)'] as const)
                  : windowDays !== null && windowDays <= 2
                    ? (['var(--red-bg1)', 'var(--red-fg1)'] as const)
                    : windowDays !== null && windowDays <= 5
                      ? (['var(--yellow-bg1)', 'var(--yellow-fg1)'] as const)
                      : (['var(--brand-shade-bg)', 'var(--brand-90)'] as const)
              return (
                <span
                  className={styles.windowMsg}
                  data-testid="window-closes-msg"
                  style={{ background: tone[0], color: tone[1] }}
                >
                  {closed ? 'Applications closed' : (ahead ?? 'Deadline passed · still open')}
                </span>
              )
            })()}
          {demand.requestor_name && (
            <>
              <span className={styles.divider}>·</span>
              <span>
                Requested by <strong>{demand.requestor_name}</strong>
              </span>
            </>
          )}
          {(demand.submission_date ?? demand.created_at) && (
            <>
              <span className={styles.divider}>·</span>
              {/* Prefer the real submission_date; created_at is the DB insert
                  time (= the import date for migrated demands), which is not
                  when the demand was actually submitted. */}
              <span>
                Submitted{' '}
                {new Date(demand.submission_date ?? demand.created_at).toLocaleDateString()}
              </span>
            </>
          )}
        </div>
      </header>

      <div className={styles.actions}>
        <DemandActions
          demand={demand}
          isCouncil={isCouncil || isAdminLike}
          canEdit={canEditDemand}
          canStepOnly={canEditFields && !canEditDemand}
          myApplication={myApplication}
          hasPendingInvitation={!!myPendingInvitation}
          onAcceptInvitation={() =>
            myPendingInvitation &&
            respondInvitation.mutate(
              { invitationId: myPendingInvitation.id, action: 'accept' },
              {
                onSuccess: () =>
                  toast.showToast({ title: 'Invitation accepted', intent: 'success' }),
                // The hook's own onError only refetches — silent failure here
                // reads as success, which is the bug TalentDashboard already
                // had to fix on its copy of this button.
                onError: (err) =>
                  toast.showToast({
                    title: "Couldn't accept the invitation",
                    body: apiErrorMessage(err),
                    intent: 'error',
                  }),
              },
            )
          }
          onTransition={handleTransition}
          onOpenResolve={() => setResolveOpen(true)}
          onInvite={() => navigate(`/talents?inviteFor=${demand.id}`)}
          onApply={() => setApplyOpen(true)}
          onStaff={markStaffed}
        />
        {canEditFields && demand.stage === 'new' && (
          <Button
            appearance="primary"
            disabled={transitionDemand.isPending}
            onClick={() =>
              transitionDemand.mutate(
                { id: demand.id, body: { to_stage: 'scoping' } },
                {
                  onSuccess: () => toast.showToast({ title: 'Review started' }),
                  onError: (err: unknown) => {
                    // B4 fires here — every demand is created caretaker-less,
                    // so this is the most common place a block is hit. It used
                    // to discard the reason entirely, which is #977.
                    const warnings = parseStageWarnings(err)
                    if (warnings) {
                      setStageWarnings(warnings)
                      setWarnedTransition({ body: { to_stage: 'scoping' } })
                      return
                    }
                    toast.showToast({
                      title: apiErrorMessage(err, 'Failed to start review'),
                      intent: 'error',
                    })
                  },
                },
              )
            }
            data-testid="action-start-review"
          >
            Start review
          </Button>
        )}
        {canEditDemand && demand.stage !== 'closed' && (
          <Button
            appearance="subtle"
            onClick={() => setChangeStageOpen(true)}
            data-testid="action-change-stage"
          >
            Change stage…
          </Button>
        )}
        {canEditDemand && demand.stage === 'closed' && (
          <Button
            appearance="subtle"
            onClick={() => setReopenOpen(true)}
            data-testid="action-reopen"
          >
            Reopen…
          </Button>
        )}
        {canWithdraw && !pendingWithdrawal && (
          <Button
            appearance="subtle"
            onClick={() => setWithdrawOpen(true)}
            data-testid="action-withdraw"
          >
            Withdraw…
          </Button>
        )}
        {/* A caretaker may close their demand (#1268) but holds no Change
            stage…, which is how council reaches the same dialog. So they get
            the dialog directly, at any live stage. */}
        {canClose && !canEditDemand && (
          <Button
            appearance="subtle"
            onClick={() => setResolveOpen(true)}
            data-testid="action-caretaker-resolve"
          >
            Resolve…
          </Button>
        )}
        {/* Promote bridge (v3.0.0): a promoted demand can graduate into a
            Product. Deep-links to the product create dialog, prefilled. */}
        {canEditDemand &&
          demand.stage === 'closed' &&
          demand.resolution_type === 'promoted' && (
            <Button
              appearance="primary"
              onClick={() => navigate(`/products?from=${demand.id}`)}
              data-testid="action-create-product"
            >
              Create product…
            </Button>
          )}
        {canEditDemand && (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<MoreHorizontal20Regular />}
                aria-label="More actions"
                data-testid="demand-more-actions"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {/* The same Route action the audit queue offers (#1292),
                    reachable after a demand leaves New (#1319): the audit
                    queue only lists New-stage demands, so a mid-pipeline
                    move — a UN demand someone advanced, a wrong landing
                    noticed late — had no UI at all and fell to ops scripts. */}
                <MenuItem
                  onClick={() => setRouteDialogOpen(true)}
                  data-testid="action-route-demand"
                >
                  Move to another focus area…
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    toast.showToast({ title: 'Archive coming soon', intent: 'info' })
                  }
                  data-testid="action-archive"
                >
                  Archive
                </MenuItem>
                <MenuItem
                  onClick={async () => {
                    try {
                      const res = await api.get('/demands/export', { responseType: 'blob' })
                      downloadBlob('daccelerate-demands.xlsx', res.data as Blob)
                    } catch (e) {
                      toast.showToast({
                        title: 'Export failed',
                        body: await blobErrorMessage(e),
                        intent: 'error',
                      })
                    }
                  }}
                  data-testid="action-export"
                >
                  Export demands (.xlsx)
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    try {
                      navigator.clipboard?.writeText(window.location.href)
                      toast.showToast({ title: 'Link copied' })
                    } catch {
                      toast.showToast({ title: 'Copy failed', intent: 'error' })
                    }
                  }}
                  data-testid="action-copy-link"
                >
                  Copy link
                </MenuItem>
                {canDelete && (
                  <MenuItem
                    onClick={() => setDeleteOpen(true)}
                    data-testid="action-delete"
                    style={{ color: bucketColors.highPriority }}
                  >
                    Delete demand…
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </div>

      <TabList
        className={styles.tabList}
        selectedValue={effectiveTab}
        onTabSelect={(_e: SelectTabEvent, data: SelectTabData) => selectTab(data.value as TabKey)}
        data-testid="demand-detail-tabs"
      >
        {visibleTabs.map((t) => (
          <Tab key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
            {t.label}
            {/* A draft that survives a tab switch but says nothing is only
                half a fix — you would have to guess it was still there. */}
            {t.key === 'priority' && isPriorityDraftDirty(priorityDraft) && (
              <Badge
                appearance="filled"
                color="warning"
                size="small"
                style={{ marginLeft: 6 }}
                data-testid="tab-priority-unsaved"
              >
                Unsaved
              </Badge>
            )}
            {typeof t.count === 'number' && (
              <Badge
                appearance="tint"
                color="informative"
                size="small"
                style={{ marginLeft: 6 }}
              >
                {t.count}
              </Badge>
            )}
          </Tab>
        ))}
      </TabList>

      {/* Says exactly what the confirmation dialog would say, before you act.
          `outside_focus_area` is the same server rule that raises the 409, so
          the banner and the dialog cannot disagree about whether you are
          outside your area. It used to read "read-only. You can only edit
          demands in your own focus area", which stopped being true with #995. */}
      {demand.viewer?.outside_focus_area && (
        <div
          role="status"
          data-testid="cross-fa-readonly-banner"
          style={{
            padding: '8px 12px',
            marginBottom: 8,
            borderRadius: 6,
            background: tokens.colorNeutralBackground3,
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            color: tokens.colorNeutralForeground2,
            fontSize: 13,
          }}
        >
          This demand is in {demand.focus_area_code || 'another focus area'}, not{' '}
          {effectiveUser?.focus_area_code || 'your focus area'}. You can make changes — each one is
          recorded under your name.
        </div>
      )}

      <div className={styles.detailGrid}>
        <div className={styles.body}>
          {effectiveTab === 'overview' && (
            <>
              <OverviewTab
                demand={demand}
                canEdit={canEditFields}
                canEditRequest={canEditRequest}
                onSave={handleSave}
                showIntakeAnswers={!isCouncil}
              />
              {/* Council reads the discussion in the Timeline tab, next to the
                  stage history and field edits. Everyone else gets it here —
                  the server sends them their own messages and mentions
                  (#995, decided 2026-09-22). */}
              {!isCouncil && <DemandDiscussion demandId={demand.id} />}
            </>
          )}
          {effectiveTab === 'business' && (
            <BusinessCaseTab demand={demand} canEdit={canEditDemand} onSave={handleSave} />
          )}
          {effectiveTab === 'talent' && (
            <TalentTab
              demand={demand}
              applications={isCouncil ? applications : []}
              isCouncil={isCouncil}
              canEdit={canEditDemand}
              onSave={handleSave}
            />
          )}
          {effectiveTab === 'timeline' && <TimelineTab demand={demand} />}
          {effectiveTab === 'priority' && (
            <PriorityTab
              demand={demand}
              canEdit={canEditDemand}
              onSave={handleSave}
              onSaveMany={handleSaveMany}
              draft={priorityDraft}
              onDraftChange={setPriorityDraft}
            />
          )}
        </div>

        <aside className={styles.sidebar}>
          <DetailSidebar
            demand={demand}
            readiness={readiness}
            canEdit={canEditDemand}
            canEditFields={canEditFields}
            onMarkStaffed={markStaffed}
            markingStaffed={staffDemand.isPending}
            contributors={[
              // From the demand, not from the applications list (#1044). Same
              // names as before — the server now sends them directly, so a
              // non-council viewer still sees who is on the work without the
              // response also carrying everyone's email and cover letter.
              ...(demand.contributors ?? []).map((c) => ({ id: c.id, name: c.name })),
              ...(demand.freehand_members ?? []).map((name, i) => ({
                id: -(i + 1),
                name,
                freehand: true,
              })),
            ]}
          />
        </aside>
      </div>

      {resolveOpen && (
        <ResolveDialog
          open={resolveOpen}
          demandId={demand.id}
          demandRef={demand.demand_id || `DEM-${demand.id}`}
          demandTitle={demand.title}
          onClose={() => setResolveOpen(false)}
          mode={demand.stage === 'closed' ? 'change' : 'resolve'}
        />
      )}
      {reopenOpen && (
        <ReopenDialog
          open={reopenOpen}
          demand={demand}
          onClose={() => setReopenOpen(false)}
        />
      )}
      {withdrawOpen && (
        <WithdrawDialog
          open={withdrawOpen}
          demand={demand}
          onClose={() => setWithdrawOpen(false)}
          onWithdrawn={(d) =>
            toast.showToast({
              title:
                d.stage === 'closed'
                  ? 'Demand withdrawn — closed'
                  : 'Withdrawal requested — sent to council',
            })
          }
        />
      )}
      <TransitionDialog
        open={!!pendingTransition}
        demand={demand}
        toStage={pendingTransition}
        onClose={() => setPendingTransition(null)}
        onConfirm={confirmTransition}
        busy={transitionDemand.isPending}
      />
      <ChangeStageDialog
        open={changeStageOpen}
        onOpenChange={setChangeStageOpen}
        currentStage={demand.stage}
        focusAreaCode={demand.focus_area_code}
        demandId={demand.id}
        onConfirm={handleStageOverride}
        onOpenResolve={() => setResolveOpen(true)}
        pending={transitionDemand.isPending}
      />
      <RouteDemandDialog
        demand={routeDialogOpen ? demand : null}
        onClose={() => setRouteDialogOpen(false)}
        actorId={effectiveUser?.id}
      />
      <DeleteDemandDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        demandRef={demand.demand_id || `DEM-${demand.id}`}
        demandTitle={demand.title}
        onConfirm={handleDelete}
        pending={deleteDemand.isPending}
      />
      <ApplyDialog
        demand={demand}
        demandTitle={demand.title}
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        submitting={createApplication.isPending}
        onSubmit={async (input) => {
          createApplication.mutate(
            { demand_id: demand.id, ...input },
            {
              onSuccess: () => {
                toast.showToast({ title: `Application submitted for "${demand.title}"` })
                setApplyOpen(false)
              },
              onError: (err) => {
                toast.showToast({
                  title: 'Failed to submit application',
                  body: apiErrorMessage(err),
                  intent: 'error',
                })
              },
            },
          )
        }}
      />
      <StageWarningDialog
        warnings={stageWarnings}
        toStage={warnedTransition?.body.to_stage ?? null}
        busy={transitionDemand.isPending}
        onContinue={continuePastWarnings}
        onGoFix={dismissWarnings}
      />
      <Dialog
        open={caretakerPromptOpen}
        onOpenChange={(_, d) => setCaretakerPromptOpen(d.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Assign caretaker before posting</DialogTitle>
            <DialogContent>
              <Text block style={{ marginBottom: 12 }}>
                A demand needs a caretaker before it can leave New. Pick a
                council member from {demand.focus_area_code}.
              </Text>
              <Dropdown
                aria-label="Caretaker"
                placeholder="Select a caretaker…"
                value={
                  caretakerCandidates.find((u) => u.id === pickedCaretakerId)?.name ?? ''
                }
                selectedOptions={
                  pickedCaretakerId != null ? [String(pickedCaretakerId)] : []
                }
                onOptionSelect={(_, data) => {
                  const id = data.optionValue
                    ? Number.parseInt(data.optionValue, 10)
                    : null
                  setPickedCaretakerId(Number.isFinite(id) ? (id as number) : null)
                }}
                style={{ width: '100%' }}
              >
                {caretakerCandidates
                  // super_admin included: the endpoint returns them
                  // deliberately, and dropping them left the dropdown empty —
                  // with "Assign & continue" disabled and nothing saying why —
                  // on any focus area whose only admin-level member is one.
                  .filter(
                    (u) =>
                      u.role === 'council' || u.role === 'admin' || u.role === 'super_admin',
                  )
                  .map((u) => (
                    <Option key={u.id} value={String(u.id)} text={u.name}>
                      {u.name}
                    </Option>
                  ))}
              </Dropdown>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setCaretakerPromptOpen(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={pickedCaretakerId == null || updateDemand.isPending}
                onClick={confirmCaretakerAssignment}
              >
                Assign &amp; continue
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

interface DemandActionsProps {
  demand: Demand
  isCouncil: boolean
  canEdit?: boolean
  /** May edit, but without the council surface: offer the stage's step only. */
  canStepOnly?: boolean
  myApplication?: { status: string; feedback?: string | null }
  hasPendingInvitation?: boolean
  onAcceptInvitation?: () => void
  onTransition: (toStage: DemandStage, message: string) => void
  onOpenResolve: () => void
  onInvite?: () => void
  onApply?: () => void
  onStaff?: () => void
}

// ── Sidebar: Stage info + People (mockup §7729-7865) ──

// The sidebar's target labels derive from lib/demandSla's red thresholds
// (#1156) — this map was a third copy of the numbers.

interface DetailSidebarProps {
  demand: Demand
  /** The between-moves checklist (#1207); undefined for viewers outside the
   * edit trio (the endpoint 403s them and the fetch is gated off). */
  readiness?: import('../hooks/useDemandReadiness').DemandReadiness
  /** Council surface: team management, freehand linking. */
  canEdit: boolean
  /** PATCH surface: person pickers, headcount, app window. canEdit plus the
   * demand's caretaker (ticket #14 — backend can_edit_demand admits them). */
  canEditFields: boolean
  /** The toolbar's Mark Staffed handler, so the card's button does the same thing. */
  onMarkStaffed: () => void
  markingStaffed: boolean
  contributors: { id: number; name: string; freehand?: boolean }[]
}

function DetailSidebar({
  demand,
  readiness,
  canEdit,
  canEditFields,
  onMarkStaffed,
  markingStaffed,
  contributors,
}: DetailSidebarProps) {
  const styles = useStyles()
  const effectiveUser = useEffectiveUser()
  // Read-only viewers get names from `currentName` on each PersonPicker;
  // only editors open the dropdown this list feeds (#1153).
  const { data: mentionableUsers = [] } = useMentionableUsers(demand.focus_area_code, canEditFields)
  const updateDemand = useUpdateDemand()
  const staffDemand = useStaffDemand()
  const createComment = useCreateComment()
  const toast = useToast()
  const daysInStage = daysBetween(demand.stage_entered_at ?? demand.submission_date)
  const urgency = stageUrgency(demand.stage, daysInStage)
  const windowDays = daysUntil(demand.application_closes_at)
  const slaLeft = daysInStage === null ? null : slaDaysLeft(demand.stage, daysInStage)

  function handlePersonChange(patch: Partial<DemandUpdateInput>) {
    updateDemand.mutate(
      { id: demand.id, data: patch },
      {
        onError: (err) =>
          toast.showToast({
            title: 'Failed to update person',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  // Add a contributor straight from the People panel — same backend as the
  // Talent tab's "Add team member". Allowed from under_review onward.
  const addMember = useAddTeamMember()
  const canAddMember =
    canEdit &&
    (demand.stage === 'scoping' ||
      demand.stage === 'triaging' ||
      demand.stage === 'resourcing')
  // Fetch whenever the user can edit — the directory feeds both "Add member"
  // and the freehand→account link picker.
  const { data: talentDir } = useTalentDirectory({ enabled: canEdit })
  const talentOptions: MentionableUser[] = (talentDir?.items ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    email: t.email,
    role: 'talent',
  }))

  // Freehand members that could be linked to an account — fuzzy suggestions.
  const hasFreehand = (demand.freehand_members ?? []).length > 0
  const { data: freehandSuggestions = [] } = useFreehandSuggestions(
    demand.id,
    canEdit && hasFreehand,
  )
  const suggestionsByName = new Map(
    freehandSuggestions.map((s) => [s.freehand_name, s.candidates]),
  )

  function handleAddMember(args: { talentId?: number; name?: string }) {
    addMember.mutate(
      { demandId: demand.id, ...args },
      {
        onSuccess: () => toast.showToast({ title: 'Contributor added' }),
        onError: (err: unknown) => {
          toast.showToast({ title: apiErrorMessage(err, 'Failed to add contributor'), intent: 'error' })
        },
      },
    )
  }

  function handleAppWindowChange(_ev: unknown, data: { value?: number | null; displayValue?: string }) {
    // Typed input arrives as displayValue only; the arrows send value.
    const newDays = data.value ?? (data.displayValue ? Number.parseInt(data.displayValue, 10) : null)
    if (newDays == null || Number.isNaN(newDays) || newDays < 1) return
    const newClosesAt = new Date(Date.now() + newDays * 86_400_000).toISOString()
    updateDemand.mutate(
      { id: demand.id, data: { application_closes_at: newClosesAt } },
      {
        onSuccess: () => {
          createComment.mutate({
            demandId: demand.id,
            body: { body: `📅 Application deadline moved to ${newDays} day${newDays === 1 ? '' : 's'} from today.` },
          })
        },
        onError: (err) =>
          toast.showToast({
            title: 'Failed to move the deadline',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  // Closing is a decision, not a date: the deadline only reminds (2026-10-16).
  // The timeline shows who did it, from the field audit.
  function handleApplicationsOpen(open: boolean) {
    updateDemand.mutate(
      { id: demand.id, data: { applications_open: open } },
      {
        onSuccess: () =>
          toast.showToast({ title: open ? 'Applications reopened' : 'Applications closed' }),
        onError: (err) =>
          toast.showToast({
            title: open ? 'Failed to reopen applications' : 'Failed to close applications',
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  return (
    <>
      {/* Stage info */}
      <section className={styles.sideSection}>
        <h3 className={styles.sideSectionTitle}>Classification</h3>
        <div className={styles.sideKvRow}>
          <span className={styles.sideKvKey}>Type</span>
          <Badge appearance="tint" color="brand" size="small">
            {DEMAND_TYPE_LABELS[demand.demand_type ?? 'new_demand'] ?? demand.demand_type}
          </Badge>
        </div>
        {demand.demand_type === 'change_request' && (
          <div className={styles.sideKvRow}>
            <span className={styles.sideKvKey}>Changes</span>
            <span className={styles.sideKvValue}>
              {demand.linked_product_id != null ? (
                <a href={`/products/${demand.linked_product_id}`}>
                  Product #{demand.linked_product_id}
                </a>
              ) : (
                demand.linked_freehand || '—'
              )}
            </span>
          </div>
        )}
        <div className={styles.sideKvRow}>
          <span className={styles.sideKvKey}>Support</span>
          <Badge
            appearance="tint"
            color={demand.support_mode === 'self_supported' ? 'informative' : 'subtle'}
            size="small"
          >
            {SUPPORT_MODE_LABELS[demand.support_mode ?? 'council_supported'] ?? demand.support_mode}
          </Badge>
        </div>
      </section>

      {(() => {
        const st = deriveStaffing(demand)
        const statusColor =
          st.status === 'fully_staffed'
            ? 'success'
            : st.status === 'partially_staffed'
              ? 'warning'
              : st.status === 'closed'
                ? 'subtle'
                : 'informative'
        return (
          <section className={styles.sideSection}>
            <h3 className={styles.sideSectionTitle}>Staffing</h3>
            <div className={styles.sideKvRow}>
              <span className={styles.sideKvKey}>Status</span>
              <Badge appearance="tint" color={statusColor} size="small">
                {STAFFING_STATUS_LABELS[st.status]}
              </Badge>
            </div>
            <div className={styles.sideKvRow}>
              <span className={styles.sideKvKey}>Filled</span>
              <span className={styles.sideKvValue}>
                {st.filled} / {st.headcount}
              </span>
            </div>
            {/* Staffing happens outside the app for most demands (#1206:
                19/20 staffed demands had zero in-app applications), so an
                unconfirmed summary is a GUESS — invite the person who knows
                to record reality instead of presenting the guess as fact.
                One button on purpose: the 'off' override has never been used
                in the data, and an unrecorded demand staying unconfirmed is
                the correct pressure (the posted-no-apps alert keeps asking). */}
            {!st.confirmed && demand.stage === 'resourcing' && canEditFields && (
              <div data-testid="staffing-confirm-strip" style={{ marginTop: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--colorNeutralForeground3)', marginBottom: 4 }}>
                  Unconfirmed — staffing usually happens outside the app. Found people already?
                </div>
                {/* One control per viewer: council has Mark Staffed in the
                    toolbar. A caretaker who is not council gets no toolbar in
                    Resourcing (DemandActions), so the button lives here for them. */}
                {!canEdit && (
                  <Button
                    size="small"
                    appearance="secondary"
                    data-testid="staffing-mark-on"
                    disabled={markingStaffed}
                    onClick={onMarkStaffed}
                  >
                    Mark staffed
                  </Button>
                )}
              </div>
            )}
            {/* The undo, in the same place as the thing it undoes (#1245).
                Marking staffed used to be a one-way door: the frontend only
                ever wrote `on`, and the only route back was closing the demand
                and re-opening it — rewriting the whole lifecycle to take back
                one click. Clearing restores the derived state AND the stage
                clock the mark had reset, so the age badge and the SLA banner
                stop reading zero. No confirmation before the mark: 19 of 20
                staffed demands are staffed outside the app, so it is usually
                recording a fact, and a prompt would tax that every time
                without helping whoever clicked it a week ago. */}
            {demand.staffed_status === 'on' && canEditFields && (
              <div data-testid="staffing-clear-strip" style={{ marginTop: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--colorNeutralForeground3)', marginBottom: 4 }}>
                  Marked staffed by hand.
                </div>
                <Button
                  size="small"
                  appearance="subtle"
                  data-testid="staffing-clear"
                  disabled={staffDemand.isPending}
                  onClick={() =>
                    staffDemand.mutate(
                      { id: demand.id, body: { status: 'auto' } },
                      {
                        onSuccess: () =>
                          toast.showToast({ title: 'Staffed marking cleared' }),
                        onError: (err) =>
                          toast.showToast({
                            title: 'Could not clear the staffed marking',
                            body: apiErrorMessage(err),
                            intent: 'error',
                          }),
                      },
                    )
                  }
                >
                  Clear
                </Button>
              </div>
            )}
            {(st.recruiting || st.assessing) && (
              <div className={styles.sideKvRow}>
                <span className={styles.sideKvKey}>Activity</span>
                <span style={{ display: 'flex', gap: 4 }}>
                  {st.recruiting && (
                    <Badge appearance="outline" color="brand" size="small">
                      Recruiting
                    </Badge>
                  )}
                  {st.assessing && (
                    <Badge appearance="outline" color="informative" size="small">
                      Assessing
                    </Badge>
                  )}
                </span>
              </div>
            )}
            {canEditFields && (
              <div className={styles.sideKvRow}>
                <span className={styles.sideKvKey}>Headcount</span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  defaultValue={demand.headcount ?? 1}
                  data-testid="headcount-input"
                  style={{ width: 56 }}
                  onBlur={(e) => {
                    const n = Number(e.target.value)
                    if (Number.isFinite(n) && n >= 1 && n <= 99 && n !== (demand.headcount ?? 1)) {
                      updateDemand.mutate(
                        { id: demand.id, data: { headcount: n } },
                        {
                          onError: (err) =>
                            toast.showToast({
                              title: 'Failed to update headcount',
                              body: apiErrorMessage(err),
                              intent: 'error',
                            }),
                        },
                      )
                    }
                  }}
                />
              </div>
            )}
          </section>
        )
      })()}

      <section className={styles.sideSection}>
        <h3 className={styles.sideSectionTitle}>Stage info</h3>
        <div className={styles.sideKvRow}>
          <span className={styles.sideKvKey}>Current</span>
          <Badge appearance="tint" color="brand" size="small">
            {STAGE_LABEL[demand.stage] ?? demand.stage}
          </Badge>
        </div>
        {demand.stage !== 'closed' && daysInStage !== null && (
          <div className={styles.sideKvRow}>
            <span className={styles.sideKvKey}>Days in stage</span>
            <span
              className={styles.sideKvValue}
              style={{
                color: urgency === 'red' ? 'var(--colorPaletteRedForeground1)' : urgency === 'amber' ? 'var(--colorPaletteDarkOrangeForeground1)' : undefined,
              }}
            >
              {daysInStage}
            </span>
          </div>
        )}
        {/* A countdown, not the target: "60 days" sat there on day 10 and 70. */}
        {slaLeft !== null && (
          <div className={styles.sideKvRow} data-testid="stage-sla">
            <span className={styles.sideKvKey}>SLA</span>
            <span className={styles.sideKvValue}>
              {slaLeft < 0
                ? `overdue by ${-slaLeft} day${slaLeft === -1 ? '' : 's'}`
                : `${slaLeft} day${slaLeft === 1 ? '' : 's'} left`}
            </span>
          </div>
        )}
        {demand.stage === 'resourcing' && (
          <div className={styles.sideKvRow} data-testid="applications-row">
            <span className={styles.sideKvKey}>
              Applications
              <InfoHint text="Applications stay open past the deadline until you close them." />
            </span>
            <span className={styles.sideKvValue}>
              {demand.applications_closed_at ? 'Closed' : 'Open'}
              {canEditFields && (
                <Button
                  size="small"
                  appearance="subtle"
                  data-testid="applications-toggle"
                  disabled={updateDemand.isPending}
                  onClick={() => handleApplicationsOpen(!!demand.applications_closed_at)}
                >
                  {demand.applications_closed_at ? 'Reopen' : 'Close'}
                </Button>
              )}
            </span>
          </div>
        )}
        {demand.stage === 'resourcing' && windowDays !== null && (
          <div className={styles.sideKvRow}>
            <span className={styles.sideKvKey}>Deadline</span>
            {canEditFields ? (
              // Days from today. Past the deadline the box is empty rather than
              // a "1" that read as one day left; any number sets a new deadline.
              <SpinButton
                size="small"
                min={1}
                max={90}
                value={windowDays > 0 ? windowDays : null}
                placeholder="Passed"
                aria-label="Days until the application deadline"
                onChange={handleAppWindowChange}
                style={{ width: 80 }}
              />
            ) : (
              <span className={styles.sideKvValue} style={{ color: 'var(--colorBrandForeground1)' }}>
                {windowDays <= 0 ? 'Passed' : `${windowDays} day${windowDays === 1 ? '' : 's'} left`}
              </span>
            )}
          </div>
        )}
        {demand.stage === 'closed' && demand.resolution_type && (
          <div className={styles.sideKvRow}>
            <span className={styles.sideKvKey}>Resolution</span>
            <Badge
              appearance="tint"
              color={closureColor(demand.resolution_type)}
              size="small"
            >
              {resolutionTypeLabel(demand.resolution_type)}
            </Badge>
          </div>
        )}
      </section>

      {/* Readiness — the expectations engine read sideways (#1207): each
          item carries the SAME message the transition 409 would, so what
          council preps against here is exactly what the move will flag. */}
      {canEditFields && readiness && readiness.next_stage && readiness.items.length > 0 && (
        <section className={styles.sideSection} id="demand-readiness" data-testid="readiness-card">
          <h3 className={styles.sideSectionTitle}>
            Readiness · toward {STAGE_LABELS[readiness.next_stage as DemandStage] ?? readiness.next_stage}
          </h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {readiness.items.map((item) => (
              <li
                key={item.field}
                title={item.message}
                data-testid={`readiness-item-${item.field}`}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'baseline',
                  padding: '3px 0',
                  fontSize: 12,
                  color: item.met ? 'var(--colorNeutralForeground3)' : undefined,
                }}
              >
                <span aria-hidden="true" style={{ width: 14, textAlign: 'center' }}>
                  {item.met ? '✓' : '○'}
                </span>
                <span style={{ textDecoration: item.met ? 'line-through' : undefined }}>
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* People */}
      <section className={styles.sideSection} id="demand-people">
        <h3 className={styles.sideSectionTitle}>People</h3>
        <PersonPicker
          currentName={demand.requestor_name}
          roleLabel="Requestor"
          avatarColor={bayerColors.navy}
          users={mentionableUsers}
          disabled={!canEditFields}
          allowFreehand
          onSelect={(user) => {
            handlePersonChange({ requestor_name: user ? user.name : null })
          }}
          onFreehandSelect={(name) => {
            handlePersonChange({ requestor_name: name })
          }}
        />
        <PersonPicker
          currentName={demand.caretaker_name}
          userId={demand.caretaker_id}
          roleLabel="Caretaker"
          avatarColor="#6b21a8"
          users={mentionableUsers}
          disabled={!canEditFields}
          allowFreehand
          selfUser={
            effectiveUser?.id && effectiveUser.name
              ? { id: effectiveUser.id, name: effectiveUser.name }
              : null
          }
          onSelect={(user) => {
            handlePersonChange({
              caretaker_name: user ? user.name : null,
              caretaker_id: user ? user.id : null,
            })
          }}
          onFreehandSelect={(name) => {
            handlePersonChange({ caretaker_name: name, caretaker_id: null })
          }}
        />
        {/* Contributors — read-only; comes from approved applications.
            Not editable here: council changes the contributor list by
            approving / un-approving applications, not by direct edit. */}
        {contributors.map((c) =>
          c.freehand && canEdit ? (
            <FreehandLinkControl
              key={`freehand-${c.name}`}
              demandId={demand.id}
              name={c.name}
              candidates={suggestionsByName.get(c.name) ?? []}
              talentOptions={talentOptions}
            />
          ) : (
            <PersonPicker
              key={`contributor-${c.id}`}
              currentName={c.name}
              userId={c.freehand ? undefined : c.id}
              roleLabel={c.freehand ? 'Contributor (added)' : 'Contributor'}
              avatarColor="#0f766e"
              users={[]}
              disabled
              onSelect={() => undefined}
            />
          ),
        )}
        {canAddMember && (
          <div data-testid="sidebar-add-contributor">
            <PersonPicker
              currentName={null}
              roleLabel="Add contributor"
              emptyLabel="Add a contributor"
              avatarColor="#0f766e"
              users={talentOptions}
              allowFreehand
              disabled={addMember.isPending}
              onSelect={(u) => {
                if (u) handleAddMember({ talentId: u.id })
              }}
              onFreehandSelect={(name) => handleAddMember({ name })}
            />
          </div>
        )}
      </section>
    </>
  )
}

function DemandActions({
  demand,
  isCouncil,
  canEdit,
  canStepOnly,
  myApplication,
  hasPendingInvitation,
  onAcceptInvitation,
  onTransition,
  onOpenResolve,
  onInvite,
  onApply,
  onStaff,
}: DemandActionsProps) {
  // May edit but does not hold the council surface — today that is a
  // caretaker who is not council. The backend grants them exactly the
  // step-advances (STEP_ADVANCEMENTS + can_edit_demand, ticket #14), so offer
  // the step for the current stage and nothing council-only. Keyed on the
  // server's two answers rather than on who the caretaker is (#1268). Checked
  // before the talent and read-only branches, which would otherwise swallow
  // them.
  if (canStepOnly) {
    switch (demand.stage) {
      case 'scoping':
        return (
          <Button
            appearance="primary"
            data-testid="action-start-triage"
            onClick={() => onTransition('triaging', 'Scoping complete — moved to Triaging')}
          >
            Start Triage
          </Button>
        )
      case 'triaging':
        return (
          <Button
            appearance="primary"
            data-testid="action-post-for-applications"
            onClick={() => onTransition('resourcing', 'Demand posted for applications')}
          >
            Post for Applications
          </Button>
        )
      default:
        // Resourcing onward is council territory (invite/staff/resolve);
        // 'new' has its own Start-review button outside this component.
        return null
    }
  }

  // Talent view: show application status or Apply button
  if (!isCouncil) {
    // A talent can open a resourcing demand whose applications council closed,
    // and the server then refuses an application (`sql_posted()`). Same test as
    // the opportunity cards, so no button here leads to a 400. A passed
    // deadline is not a close (2026-10-16).
    const applicationsOpen = demand.stage === 'resourcing' && acceptsApplications(demand)
    if (myApplication) {
      const status = myApplication.status
      if (status === 'approved') {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Badge appearance="filled" color="success" size="medium">
              ✓ Application approved
            </Badge>
            {myApplication.feedback && (
              <Caption1 style={{ fontStyle: 'italic' }}>"{myApplication.feedback}"</Caption1>
            )}
          </div>
        )
      }
      if (status === 'rejected') {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Badge appearance="filled" color="danger" size="medium">
              Application not selected
            </Badge>
            {myApplication.feedback && (
              <Caption1 style={{ fontStyle: 'italic' }}>"{myApplication.feedback}"</Caption1>
            )}
            {applicationsOpen && (
              <Button appearance="primary" size="small" onClick={onApply}>
                Re-apply
              </Button>
            )}
          </div>
        )
      }
      // Withdrawing is the talent's own action, so being told it is "pending
      // review" is the app contradicting something they did. The backend only
      // treats pending and approved as duplicates, so re-applying is allowed
      // here for exactly the same reason it is allowed after a rejection.
      if (status === 'withdrawn') {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Badge appearance="tint" color="informative" size="medium">
              Application withdrawn
            </Badge>
            {applicationsOpen && (
              <Button appearance="primary" size="small" onClick={onApply}>
                Apply again
              </Button>
            )}
          </div>
        )
      }
      return (
        <Badge appearance="tint" color="informative" size="medium">
          Application pending review
        </Badge>
      )
    }
    // An outstanding invitation outranks the Apply button, and is checked
    // before the stage so it survives closed applications — that is precisely
    // when the two doors differ. Applying is refused once council closes
    // applications; accepting an invitation is not, because closing ends the
    // open call and an invitation is not one. Showing "Apply" here would hand
    // this talent a button that 400s while the identical button on their
    // dashboard works.
    if (hasPendingInvitation) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Button
            appearance="primary"
            data-testid="accept-invitation-cta"
            onClick={onAcceptInvitation}
          >
            Accept invitation
          </Button>
          <Caption1>You were invited to this demand — accepting joins you directly.</Caption1>
        </div>
      )
    }
    if (applicationsOpen) {
      return (
        <Button appearance="primary" data-testid="apply-cta" onClick={onApply}>
          Apply to this demand
        </Button>
      )
    }
    if (demand.stage === 'resourcing') {
      return <Caption1 data-testid="applications-closed">Applications closed</Caption1>
    }
    return <Caption1>Not open for applications</Caption1>
  }

  // Council/admin viewing but cannot edit (cross-FA) — read-only, no actions
  if (!canEdit) {
    return <Caption1>Viewing from another Focus Area (read-only)</Caption1>
  }

  switch (demand.stage) {
    case 'scoping':
      // Resourcing is reachable only via Triaging (v2.8.0) — Scoping advances
      // to Triaging, not straight to Post-for-Applications.
      return (
        <>
          <Button
            appearance="primary"
            data-testid="action-start-triage"
            onClick={() => onTransition('triaging', 'Scoping complete — moved to Triaging')}
          >
            Start Triage
          </Button>
          <Button
            appearance="secondary"
            data-testid="action-not-prioritized"
            onClick={() => onTransition('parking', 'Moved to Parking Lot')}
          >
            Parking Lot
          </Button>
        </>
      )
    case 'triaging':
      return (
        <>
          <Button
            appearance="primary"
            data-testid="action-post-for-applications"
            onClick={() => onTransition('resourcing', 'Demand posted for applications')}
          >
            Post for Applications
          </Button>
          <Button
            appearance="secondary"
            data-testid="action-not-prioritized"
            onClick={() => onTransition('parking', 'Moved to Parking Lot')}
          >
            Parking Lot
          </Button>
        </>
      )
    case 'resourcing':
      return (
        <>
          <Button
            appearance="primary"
            icon={<People16Regular />}
            data-testid="action-invite-talents"
            onClick={onInvite}
          >
            Invite talents
          </Button>
          {demand.staffed_status !== 'on' && onStaff && (
            <Button appearance="secondary" data-testid="action-mark-staffed" onClick={onStaff}>
              Mark Staffed
            </Button>
          )}
          <Button appearance="secondary" data-testid="action-resolve" onClick={onOpenResolve}>
            Resolve…
          </Button>
        </>
      )
    case 'parking':
      return (
        <Button
          appearance="primary"
          data-testid="action-reopen"
          onClick={() => onTransition('scoping', 'Demand re-opened for review')}
        >
          Re-open for review
        </Button>
      )
    case 'closed':
      if (canEdit) {
        return (
          <Button
            appearance="secondary"
            data-testid="action-change-resolution"
            onClick={onOpenResolve}
          >
            Change resolution…
          </Button>
        )
      }
      return null
    default:
      return null
  }
}

export default DemandDetail
